import React, { useMemo } from 'react';
import { Space, Tooltip, Typography } from 'antd';
import { AreaLink } from '../AreaLink';
import { MetricLink } from '../MetricLink';
import { resolveFieldRange, resolveWarningRangesForMetric } from '../../helpers/metricRules';

export interface KpiMetricStat {
    metricKey: string;
    metricName?: string;
    average?: number | null;
    avg?: number | null;
    min?: number | null;
    max?: number | null;
    sum?: number | null;
    count?: number | null;
    median?: number | null;
    variance?: number | null;
    stddev?: number | null;
    unit?: string;
    areaName?: string;
    areaId?: number;
}

type KpiField = 'average' | 'min' | 'max' | 'count' | 'sum' | 'median' | 'variance' | 'stddev';

// New interface for Report component usage
interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    onAreaClick?: (areaId: number) => void;
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

// Legacy interface for backward compatibility
interface LegacyProps {
    title?: string;
    stats: KpiMetricStat[];
    fields?: KpiField[];
}

type Props = CommonSectionProps | LegacyProps;

// Type guard to check if props are CommonSectionProps
function isCommonSectionProps(props: Props): props is CommonSectionProps {
    return 'section' in props && 'data' in props;
}

const FIELD_LABELS: Record<KpiField, string> = {
    average: 'Avg',
    min: 'Min',
    max: 'Max',
    count: 'Count',
    sum: 'Sum',
    median: 'Median',
    variance: 'Variance',
    stddev: 'Std Dev',
};

const formatNumber = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '—';
    return Number(value).toFixed(2);
};

const formatWithUnit = (value?: number | null, unit?: string) => {
    const base = formatNumber(value);
    if (!unit) return base;
    return `${base} ${unit}`;
};

const normalizeField = (field: string): KpiField | null => {
    if (field === 'avg') return 'average';
    if (Object.prototype.hasOwnProperty.call(FIELD_LABELS, field)) {
        return field as KpiField;
    }
    return null;
};

const getFieldValue = (stat: KpiMetricStat, field: KpiField) => {
    if (field === 'average') {
        return stat.average ?? stat.avg;
    }
    return stat[field];
};

const formatFieldValue = (stat: KpiMetricStat, field: KpiField) => {
    const value = getFieldValue(stat, field);
    if (value === undefined || value === null || Number.isNaN(value)) return '—';
    if (field === 'count') return value;
    return formatWithUnit(value, stat.unit);
};

const DEFAULT_PALETTE = ['#1677ff', '#2f54eb', '#52c41a', '#faad14', '#ff4d4f'];

const rangeKeyForField = (field: KpiField) => {
    if (field === 'average') return 'avg';
    if (field === 'stddev') return 'stddev';
    return field;
};

const toRgba = (hexColor: string, alpha: number) => {
    const hex = hexColor.replace('#', '').trim();
    if (hex.length === 3) {
        const r = parseInt(hex[0] + hex[0], 16);
        const g = parseInt(hex[1] + hex[1], 16);
        const b = parseInt(hex[2] + hex[2], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgba(22, 119, 255, ${alpha})`;
};

export const ReportKpiCards: React.FC<Props> = (props) => {
    // Transform data if using CommonSectionProps
    const { title, stats, fields } = useMemo(() => {
        if (isCommonSectionProps(props)) {
            // New format: transform data from section config
            const { section, data, areas, metrics } = props;
            
            // Build lookups
            const areaLookup = new Map<number, string>();
            areas.forEach((a: any) => areaLookup.set(a.id, a.name));
            
            const metricNameLookup = new Map<string, string>();
            metrics.forEach((m: any) => metricNameLookup.set(m.key, m.name));
            
            // Transform raw API data into stats format
            const transformedStats: KpiMetricStat[] = Array.isArray(data) && data.length > 0
                ? data.map((d: any) => ({
                      metricKey: d.metricKey || 'metric',
                      metricName: metricNameLookup.get(d.metricKey) || d.metricKey,
                      average: d.average ?? d.avg,
                      min: d.min,
                      max: d.max,
                      sum: d.sum,
                      count: d.count,
                      median: d.median,
                      variance: d.variance,
                      stddev: d.stddev,
                      unit: d.unit,
                      areaName: d.areaId ? areaLookup.get(d.areaId) || String(d.areaId) : undefined,
                      areaId: d.areaId,
                  }))
                : [];
            
            return {
                title: section.title,
                stats: transformedStats,
                fields: section.fields as KpiField[] | undefined,
            };
        } else {
            // Legacy format: use props directly
            return {
                title: props.title,
                stats: props.stats,
                fields: props.fields,
            };
        }
    }, [props]);
    
    const activeFields = (fields && fields.length > 0 ? fields : ['average', 'min', 'max', 'count'] as KpiField[])
        .map((field) => normalizeField(String(field)))
        .filter((field): field is KpiField => field !== null);
        
    // Get callbacks from props if using CommonSectionProps
    const onAreaClick = isCommonSectionProps(props) ? (props as CommonSectionProps).onAreaClick : undefined;
    const onMetricClick = isCommonSectionProps(props) ? (props as CommonSectionProps).onMetricClick : undefined;
    const previewAreaId = isCommonSectionProps(props) ? (props as CommonSectionProps).previewAreaId : 0;
    const metrics = isCommonSectionProps(props) ? (props as CommonSectionProps).metrics : [];

    const metricConfigByKey = useMemo(() => {
        const map = new Map<string, any>();
        metrics.forEach((metric: any) => {
            if (metric?.key) map.set(metric.key, metric);
        });
        return map;
    }, [metrics]);

    const areaById = useMemo(() => {
        const map = new Map<number, any>();
        if (isCommonSectionProps(props)) {
            (props as CommonSectionProps).areas.forEach((area: any) => {
                if (area?.id) map.set(area.id, area);
            });
        }
        return map;
    }, [props]);

    const getWarningInfo = (metricKey: string, field: KpiField, areaId?: number, value?: number | null) => {
        const config = metricConfigByKey.get(metricKey);
        const targetArea = areaId ? areaById.get(areaId) : areaById.get(previewAreaId);
        const ranges = resolveWarningRangesForMetric(config, targetArea);
        const rangeKey = rangeKeyForField(field);
        const range = resolveFieldRange(ranges, rangeKey) || ranges?.[rangeKey];
        if (!range || value === undefined || value === null || Number.isNaN(value)) {
            return { isWarning: false, message: null, range: null };
        }
        const min = range.min ?? null;
        const max = range.max ?? null;
        const isLow = min !== null && value < min;
        const isHigh = max !== null && value > max;
        const label = `Warning range ${min ?? '—'} to ${max ?? '—'}`;
        return {
            isWarning: isLow || isHigh,
            message: isLow ? `Below min (${min})` : isHigh ? `Above max (${max})` : label,
            range: { min, max },
        };
    };
    
    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {stats.map((stat) => {
                const metricConfig = metricConfigByKey.get(stat.metricKey);
                const paletteRaw = metricConfig?.colorPalette;
                const palette = paletteRaw && paletteRaw.length > 0 ? paletteRaw : DEFAULT_PALETTE;
                const accentColor = palette.find((color: string) => color) || DEFAULT_PALETTE[0];
                return (
                    <div
                        key={`${stat.metricKey}-${stat.areaName || ''}`}
                        className="report-kpi-surface"
                        style={
                            {
                                '--report-kpi-accent': accentColor,
                                '--report-kpi-accent-soft': toRgba(accentColor, 0.12),
                            } as React.CSSProperties
                        }
                    >
                        <div className="report-kpi-surface-head">
                            <div className="report-kpi-surface-title">
                                {onMetricClick ? (
                                    <MetricLink
                                        metricKey={stat.metricKey}
                                        metricName={stat.metricName || stat.metricKey}
                                        metricId={metricConfig?.id}
                                        areaId={stat.areaId || previewAreaId}
                                        onClick={onMetricClick}
                                    />
                                ) : (
                                    <Typography.Text strong>
                                        {stat.metricName || stat.metricKey}
                                    </Typography.Text>
                                )}
                            </div>
                            <div className="report-kpi-surface-meta">
                                {stat.areaName ? (
                                    onAreaClick && stat.areaId ? (
                                        <AreaLink
                                            areaId={stat.areaId}
                                            areaName={stat.areaName}
                                            onClick={onAreaClick}
                                            style={{ fontSize: 12 }}
                                        />
                                    ) : (
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {stat.areaName}
                                        </Typography.Text>
                                    )
                                ) : null}
                            </div>
                        </div>
                        <div className="report-kpi-surface-grid">
                            {activeFields.map((field) => {
                                const numericValue = getFieldValue(stat, field) ?? null;
                                const warningInfo = getWarningInfo(stat.metricKey, field, stat.areaId || previewAreaId, numericValue);
                                const formattedValue = formatFieldValue(stat, field);
                                const metricId = metricConfig?.id;
                                const areaId = stat.areaId || previewAreaId;
                                const warningValueNode =
                                    warningInfo.isWarning && onMetricClick ? (
                                        <span className="report-kpi-stat-value report-kpi-stat-value-warning">
                                            <MetricLink
                                                metricKey={stat.metricKey}
                                                metricName={String(formattedValue)}
                                                metricId={metricId}
                                                areaId={areaId}
                                                onClick={onMetricClick}
                                                style={{ color: '#ff4d4f', fontWeight: 600 }}
                                            />
                                        </span>
                                    ) : warningInfo.isWarning ? (
                                        <span className="report-kpi-stat-value report-kpi-stat-value-warning">{formattedValue}</span>
                                    ) : (
                                        <span className="report-kpi-stat-value">{formattedValue}</span>
                                    );
                                const statNode = (
                                    <div
                                        className={`report-kpi-stat ${warningInfo.isWarning ? 'report-kpi-stat-warning' : ''}`}
                                    >
                                        <div className="report-kpi-stat-label">{FIELD_LABELS[field]}</div>
                                        {warningValueNode}
                                    </div>
                                );
                                return (
                                    <React.Fragment key={field}>
                                        {warningInfo.isWarning ? (
                                            <Tooltip title={warningInfo.message || 'Out of range'}>{statNode}</Tooltip>
                                        ) : (
                                            statNode
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </Space>
    );
};

export default ReportKpiCards;
